import React from 'react';
import { Persona, Popover, PopoverTrigger, PopoverSurface } from '@fluentui/react-components';
import { Spinner } from '@fluentui/react-components';
import {useGetBasicStorefrontQuery} from "../../Api/Api";
import { Avatar } from '@fluentui/react-components';
import StorefrontSignIn from './StorefrontSignIn';
import { Person24Regular } from '@fluentui/react-icons';
import { useLocalization } from '../Helpers/useLocalization';
import { elementStyleFrom } from '../Helpers/searchPageConfig';
import { ISearchElementStyle } from '../Helpers/searchPageConfig';

// The header's signed-out account control: a "Guest / Sign in" persona that opens the storefront's
// email login. Its look is merchant-configurable through headerConfig.account* (see
// Components/Helpers/headerFooterConfig.ts) — the defaults below are the historical hardcoded pill,
// so a storefront that configures nothing renders exactly as before.
interface ILoginPopoverProps {
    /** headerConfig.account — the pill/trigger. */
    triggerStyle?: ISearchElementStyle;
    /** headerConfig.accountName — the primary line ("Guest"). */
    nameStyle?: ISearchElementStyle;
    /** headerConfig.accountSubtext — the secondary line ("Sign in"). */
    subtextStyle?: ISearchElementStyle;
    /** headerConfig.accountAvatar — the round avatar. */
    avatarStyle?: ISearchElementStyle;
    /** headerConfig.accountPanel — the popover surface holding the form. */
    panelStyle?: ISearchElementStyle;
}

const LoginPopover: React.FC<ILoginPopoverProps> = (props) => {
    const L = useLocalization();
    const organization = localStorage.getItem('organization') ?? undefined;
    const storefront = localStorage.getItem('storefront') ?? undefined;
    const sSettings = useGetBasicStorefrontQuery({ organization: organization ?? '', storefront: storefront ?? '' }, {skip: !organization || !storefront});

    const onSignup = () => {
        window.location.href = `/${organization}/${storefront}/signup`;
    }

    // Signing in from the header replaces the guest session in place: the shopper stays exactly where
    // they were, which is the point of finishing the sign-in without leaving the page. The token page
    // is what owns writing the session to local storage, so hand off to it with a redirect back here.
    const onSignedIn = (token: string) => {
        const back = window.location.pathname + window.location.search;
        window.location.assign(`/token?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent(back)}`);
    }

    // Persona renders its two lines as plain text nodes, so the only way to style them individually is
    // to hand it pre-wrapped elements. Skipped entirely when unconfigured so the default typography
    // (and the golden header render) is untouched.
    const nameCss = elementStyleFrom(props.nameStyle);
    const subtextCss = elementStyleFrom(props.subtextStyle);
    const primary = Object.keys(nameCss).length
        ? <span style={nameCss}>{L('localization_auth_guest')}</span>
        : L('localization_auth_guest');
    const secondary = Object.keys(subtextCss).length
        ? <span style={subtextCss}>{L('localization_auth_sign_in')}</span>
        : L('localization_auth_sign_in');

    return(
    <div>
        <Popover>
            <PopoverTrigger>
                <div style={{display: 'flex', alignItems: 'center', cursor: 'pointer', backgroundColor: 'lightgrey', borderRadius: 15, padding: '0.3em 1em', ...elementStyleFrom(props.triggerStyle)}}>
                    <Persona
                        primaryText={primary}
                        secondaryText={secondary}
                        style={{cursor:'pointer'}}
                        avatar={<Avatar icon={<Person24Regular />} style={elementStyleFrom(props.avatarStyle)}/>}
                    />
                </div>
            </PopoverTrigger>
            <PopoverSurface style={elementStyleFrom(props.panelStyle)}>
                <div>
                    <div className='login-box'>
                        {sSettings.isLoading && <Spinner label={L('localization_common_loading')} />}
                        {sSettings.error && <>{L('localization_auth_error_retry')}</>}
                        {sSettings.data && <>
                            <h2>{L('localization_auth_login_to')} {sSettings.data?.name}</h2>
                            <StorefrontSignIn
                                organization={organization ?? ''}
                                storefront={storefront ?? ''}
                                method={sSettings.data.loginMethod}
                                allowSignup={!!sSettings.data.allowSignup}
                                onSignup={onSignup}
                                onSignedIn={onSignedIn}
                                L={L}
                            />
                        </>}
                    </div>
                </div>
            </PopoverSurface>
        </Popover>
    </div>)
}

export default LoginPopover;
